import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, Users, TrendingUp, Mail, BarChart3, Activity, Shield, Trash2 } from "lucide-react";
import ContactInbox from "@/components/ContactInbox";
import FeedbackInbox from "@/components/FeedbackInbox";

const API = process.env.REACT_APP_BACKEND_URL;

const EXP_LABEL = {
  debutant: "Débutant", intermediaire: "Intermédiaire",
  semi_pro: "Semi-pro", pro: "Pro",
};
const CAP_LABEL = {
  "0": "Demo", "1k_5k": "$1k-5k", "5k_25k": "$5k-25k",
  "25k_100k": "$25k-100k", "100k_plus": "$100k+",
};

function StatCard({ icon: Icon, label, value, sub, color = "text-white", testid }) {
  return (
    <div className="panel p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
        <Icon size={14} className="text-white/30" />
      </div>
      <div className={`font-heading font-bold text-2xl ${color}`}>{value}</div>
      {sub && <div className="text-xs text-white/50 mt-1">{sub}</div>}
    </div>
  );
}

export default function FounderAdmin() {
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [acting, setActing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const authHeader = { headers: { Authorization: `Bearer ${token}` } };
      const [appsRes, statsRes] = await Promise.all([
        axios.get(
          filter === "all" ? `${API}/api/beta-applications` : `${API}/api/beta-applications?status=${filter}`,
          authHeader,
        ),
        axios.get(`${API}/api/founder/stats`, authHeader),
      ]);
      setData(appsRes.data);
      setStats(statsRes.data);
      try {
        const actRes = await axios.get(`${API}/api/beta-applications/activity`, authHeader);
        const map = {};
        (actRes.data.testers || []).forEach((t) => { map[t.email] = t; });
        setActivity({ map, summary: actRes.data.summary });
      } catch { setActivity(null); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally { setLoading(false); }
  }, [filter, token]);

  useEffect(() => { load(); }, [load]);

  const act = async (email, action) => {
    setActing(email);
    try {
      await axios.patch(`${API}/api/beta-applications/${encodeURIComponent(email)}/${action}`,
        {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(action === "approve" ? `${email} approuvé` : `${email} rejeté`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setActing(null); }
  };

  const remove = async (email) => {
    if (!window.confirm(`Supprimer définitivement la candidature de ${email} ? Cette action est irréversible.`)) return;
    setActing(email);
    try {
      await axios.delete(`${API}/api/beta-applications/${encodeURIComponent(email)}`,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Candidature de ${email} supprimée`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de suppression");
    } finally { setActing(null); }
  };

  const isFounder = user?.is_founder || user?.tier === "api";
  if (!isFounder) {
    return (
      <div className="p-8 text-center text-white/50" data-testid="founder-admin-forbidden">
        Accès réservé au founder. Votre tier : {user?.tier}
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="founder-admin-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold flex items-center gap-2">
          <Shield size={24} /> Founder Admin
        </h1>
        <p className="text-white/50 text-sm mt-1">Vue d&apos;ensemble de la plateforme ANATRADEX</p>
      </div>

      {/* STATS OVERVIEW — les vrais chiffres qui comptent */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="founder-stats-grid">
          <StatCard
            icon={Users}
            label="Adhérents"
            value={stats.users.total.toLocaleString("fr-FR")}
            sub={`+${stats.users.new_7d} sur 7 jours`}
            color="text-white"
            testid="stat-users-total"
          />
          <StatCard
            icon={Activity}
            label="Actifs 24h"
            value={stats.users.active_24h}
            sub={`${stats.users.on_trial} en essai`}
            color="text-up"
            testid="stat-users-active"
          />
          <StatCard
            icon={TrendingUp}
            label="Beta pending"
            value={stats.beta_applications.pending}
            sub={`${stats.beta_applications.high_score_pending} score ≥ 7`}
            color="text-warn"
            testid="stat-beta-pending"
          />
          <StatCard
            icon={CheckCircle2}
            label="Beta approuvés"
            value={stats.beta_applications.approved}
            sub={`${stats.beta_applications.total} candidatures totales`}
            color="text-up"
            testid="stat-beta-approved"
          />
          <StatCard
            icon={Mail}
            label="Newsletter"
            value={stats.newsletter.total}
            sub={`+${stats.newsletter.new_7d} cette semaine`}
            color="text-accent-blue"
            testid="stat-newsletter"
          />
          <StatCard
            icon={BarChart3}
            label="Signaux 24h"
            value={stats.signals.last_24h}
            sub={`${stats.signals.total} total plateforme`}
            color="text-white"
            testid="stat-signals"
          />
        </div>
      )}

      {/* Tiers breakdown */}
      {stats?.users?.by_tier && (
        <div className="panel p-4 mb-8" data-testid="founder-tiers-breakdown">
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Répartition par forfait</div>
          <div className="flex flex-wrap gap-4 text-sm">
            {Object.entries(stats.users.by_tier).map(([tier, count]) => (
              <div key={tier} className="flex items-center gap-2" data-testid={`tier-${tier}`}>
                <span className="text-white/60 uppercase tracking-widest text-[10px]">{tier}</span>
                <span className="font-mono font-semibold text-white">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tester onboarding funnel */}
      {activity?.summary && activity.summary.approved > 0 && (
        <div className="panel p-4 mb-8" data-testid="founder-tester-funnel">
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Suivi testeurs approuvés — funnel d'activation</div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <div>Approuvés : <b className="font-mono">{activity.summary.approved}</b></div>
            <div>Compte créé : <b className={`font-mono ${activity.summary.with_account > 0 ? "text-up" : "text-white/50"}`}>{activity.summary.with_account}</b></div>
            <div>Trades importés : <b className={`font-mono ${activity.summary.with_import > 0 ? "text-up" : "text-warn"}`}>{activity.summary.with_import}</b></div>
            <div>Rapport généré : <b className={`font-mono ${activity.summary.with_report > 0 ? "text-up" : "text-white/50"}`}>{activity.summary.with_report}</b></div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl font-bold flex items-center gap-2">
          <Users size={18} /> Beta Applications
        </h2>
        <div className="flex items-center gap-2">
          {["pending", "approved", "rejected", "all"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-xs px-3 py-2 rounded-md border ${
                filter === f ? "border-accent-blue bg-accent-blue/10 text-white" : "border-white/10 bg-black/30 text-white/60 hover:border-white/30"
              }`}
              data-testid={`filter-${f}`}
            >
              {f === "pending" ? "En attente" :
               f === "approved" ? "Approuvés" :
               f === "rejected" ? "Rejetés" : "Tous"}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-white/50"><Loader2 className="animate-spin mx-auto mb-2" /> Chargement…</div>
      ) : !data?.applications?.length ? (
        <div className="panel p-16 text-center text-white/40" data-testid="founder-empty">
          Aucune candidature pour ce filtre.
        </div>
      ) : (
        <div className="space-y-3" data-testid="founder-applications-list">
          {data.applications.map((app) => (
            <div
              key={app.email}
              className="panel p-5 flex flex-col md:flex-row md:items-start gap-4"
              data-testid={`founder-app-${app.email}`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-heading font-semibold text-lg">{app.name}</span>
                  <span className="text-white/50 text-sm">{app.email}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${
                    app.score >= 7 ? "border-up/40 bg-up/10 text-up" :
                    app.score >= 5 ? "border-accent-blue/40 bg-accent-blue/10 text-accent-blue" :
                    "border-white/10 bg-white/5 text-white/50"
                  }`}>
                    Score {app.score}/10
                  </span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-widest ${
                    app.status === "approved" ? "border-up/40 bg-up/10 text-up" :
                    app.status === "rejected" ? "border-down/40 bg-down/10 text-down" :
                    "border-warn/40 bg-warn/10 text-warn"
                  }`}>
                    {app.status === "approved" ? "APPROUVÉ" :
                     app.status === "rejected" ? "REJETÉ" : "EN ATTENTE"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/60 mb-3">
                  <span><b className="text-white">{EXP_LABEL[app.experience]}</b></span>
                  <span>Capital : <b className="text-white">{CAP_LABEL[app.capital]}</b></span>
                  <span>Broker : <b className="text-white">{app.broker || "—"}</b></span>
                  <span>Marchés : {app.markets?.join(", ") || "—"}</span>
                </div>
                <div className="text-sm text-white/70 leading-relaxed italic border-l-2 border-white/10 pl-3">
                  « {app.why} »
                </div>
                <div className="text-[10px] text-white/40 mt-2">
                  Reçu le {new Date(app.created_at).toLocaleString("fr-FR")}
                </div>
                {app.status === "approved" && activity?.map?.[app.email] && (() => {
                  const t = activity.map[app.email];
                  return (
                    <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-white/10" data-testid={`tester-activity-${app.email}`}>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${t.account_created ? "border-up/40 bg-up/10 text-up" : "border-down/40 bg-down/10 text-down"}`}>
                        {t.account_created ? "✓ Compte créé" : "✗ Pas de compte"}
                      </span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${t.imported_trades > 0 ? "border-up/40 bg-up/10 text-up" : "border-warn/40 bg-warn/10 text-warn"}`}>
                        {t.imported_trades > 0 ? `✓ ${t.imported_trades} trades importés` : "✗ Aucun import"}
                      </span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${t.reports_generated > 0 ? "border-up/40 bg-up/10 text-up" : "border-white/10 bg-white/5 text-white/50"}`}>
                        {t.reports_generated > 0 ? `✓ ${t.reports_generated} rapport(s)` : "— Aucun rapport"}
                      </span>
                      {t.followup_j3 && t.followup_j3 !== "skipped_active" && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full border border-accent-blue/40 bg-accent-blue/10 text-accent-blue">
                          Relance J+3 envoyée
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="flex md:flex-col gap-2 shrink-0">
                {app.status === "pending" && (
                  <>
                    <button
                      onClick={() => act(app.email, "approve")}
                      disabled={acting === app.email}
                      className="btn text-xs px-4 py-2 border-up/40 text-up hover:bg-up/10 disabled:opacity-40"
                      data-testid={`approve-${app.email}`}
                    >
                      {acting === app.email ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                      Approuver
                    </button>
                    <button
                      onClick={() => act(app.email, "reject")}
                      disabled={acting === app.email}
                      className="btn text-xs px-4 py-2 border-down/40 text-down hover:bg-down/10 disabled:opacity-40"
                      data-testid={`reject-${app.email}`}
                    >
                      <XCircle size={12} /> Rejeter
                    </button>
                  </>
                )}
                <button
                  onClick={() => remove(app.email)}
                  disabled={acting === app.email}
                  className="btn text-xs px-4 py-2 border-white/15 text-white/50 hover:text-down hover:border-down/40 hover:bg-down/10 disabled:opacity-40"
                  data-testid={`delete-${app.email}`}
                  title="Supprimer définitivement cette candidature"
                >
                  <Trash2 size={12} /> Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {data && (
        <div className="mt-6 text-xs text-white/40 flex justify-between">
          <span>Total : {data.total} · En attente : {data.pending}</span>
          <span className="flex items-center gap-1"><TrendingUp size={11} /> Trié par score décroissant</span>
        </div>
      )}

      <FeedbackInbox token={token} />
      <ContactInbox token={token} />
    </div>
  );
}
